/**
 * Tells a SAT e.firma (FIEL) apart from a CSD (sello digital) by inspecting
 * the certificate's extensions:
 *
 * - e.firma carries an **Extended Key Usage** extension (TLS Web Client
 *   Authentication + E-mail Protection) — it is an authentication cert.
 * - a CSD has **no** Extended Key Usage — it only seals CFDIs.
 *
 * Both share the same Key Usage bits (Digital Signature + Non Repudiation),
 * so Key Usage alone cannot distinguish them; Extended Key Usage can.
 *
 * Returns `'unknown'` whenever the bytes can't be read as a certificate so
 * callers fail open — we only ever block an upload when we are certain the
 * file is the wrong kind, never a legitimate credential.
 */
export type CertificateKind = 'fiel' | 'csd' | 'unknown';

// DER-encoded OIDs (tag 06, len 03, value bytes): id-ce-extKeyUsage 2.5.29.37
// and id-ce-keyUsage 2.5.29.15.
const EXT_KEY_USAGE_OID = [0x06, 0x03, 0x55, 0x1d, 0x25];
const KEY_USAGE_OID = [0x06, 0x03, 0x55, 0x1d, 0x0f];

const indexOfBytes = (haystack: Uint8Array, needle: number[]): number => {
	for (let i = 0; i <= haystack.length - needle.length; i += 1) {
		let matches = true;
		for (let j = 0; j < needle.length; j += 1) {
			if (haystack[i + j] !== needle[j]) {
				matches = false;
				break;
			}
		}
		if (matches) return i;
	}
	return -1;
};

const toDerBytes = (raw: ArrayBuffer): Uint8Array => {
	const bytes = new Uint8Array(raw);
	if (bytes[0] === 0x30) return bytes; // already DER (SEQUENCE)
	const text = new TextDecoder().decode(bytes);
	if (!text.includes('-----BEGIN')) return bytes;
	const base64 = text
		.replace(/-----BEGIN[^-]+-----/g, '')
		.replace(/-----END[^-]+-----/g, '')
		.replace(/\s+/g, '');
	if (!base64) return bytes;
	const binary = atob(base64);
	const out = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
	return out;
};

export const getCertificateKind = async (
	file: File,
): Promise<CertificateKind> => {
	try {
		const der = toDerBytes(await file.arrayBuffer());
		if (der[0] !== 0x30) return 'unknown'; // not a DER certificate
		if (indexOfBytes(der, EXT_KEY_USAGE_OID) !== -1) return 'fiel';
		if (indexOfBytes(der, KEY_USAGE_OID) !== -1) return 'csd';
		return 'unknown';
	} catch {
		return 'unknown';
	}
};

/**
 * What a file picked for a .cer slot is when it is certainly NOT a
 * certificate in any encoding. Plain text is never flagged: it can be a
 * base64 certificate.
 */
export type NotCertificateKind =
	| 'empty'
	| 'private_key'
	| 'compressed'
	| 'document';

const ZIP_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];
const DOCUMENT_SIGNATURES = [
	{ signature: [0x25, 0x50, 0x44, 0x46], offset: 0 }, // PDF
	{ signature: [0xff, 0xd8, 0xff], offset: 0 }, // JPEG
	{ signature: [0x89, 0x50, 0x4e, 0x47], offset: 0 }, // PNG
	{ signature: [0x66, 0x74, 0x79, 0x70], offset: 4 }, // HEIC (ftyp box)
];
// PKCS#5 and PKCS#12 password-based encryption OID arcs: they open the
// encrypted PKCS#8 structure of a SAT .key and never appear in a certificate.
const PRIVATE_KEY_ENCRYPTION_OIDS = [
	[0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x05],
	[0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x0c, 0x01],
];
const PRIVATE_KEY_HEADER_BYTES = 32;

const hasBytesAt = (bytes: Uint8Array, signature: number[], offset = 0) =>
	signature.every((value, index) => bytes[offset + index] === value);

/**
 * `null` whenever the file may be a certificate, so callers fail open just
 * like `getCertificateKind`: it only names what we are sure it is.
 */
export const getNotCertificateKind = async (
	file: File,
): Promise<NotCertificateKind | null> => {
	if (file.size === 0) return 'empty';
	try {
		const raw = await file.arrayBuffer();
		const bytes = new Uint8Array(raw);
		if (hasBytesAt(bytes, ZIP_SIGNATURE)) return 'compressed';
		if (
			DOCUMENT_SIGNATURES.some(({ signature, offset }) =>
				hasBytesAt(bytes, signature, offset),
			)
		) {
			return 'document';
		}
		const header = toDerBytes(raw).subarray(0, PRIVATE_KEY_HEADER_BYTES);
		if (PRIVATE_KEY_ENCRYPTION_OIDS.some((oid) => indexOfBytes(header, oid) !== -1)) {
			return 'private_key';
		}
		return null;
	} catch {
		return null;
	}
};
